import React, { useState, useCallback, useMemo, useEffect } from 'react';
import isNil from 'lodash/isNil';
import isBoolean from 'lodash/isBoolean';
import isEqual from 'lodash/isEqual';

const DEFAULT_PAGINATION = {
  current: 1,
  pageSize: 10,
};

export function withPagination(Component) {
  return (props) => {
    const { pagination: incomePagination, onPaginationChange, ...rest } = props;
    const previous = React.useRef(null);
    const [currentPagination, setCurrentPagination] = useState(DEFAULT_PAGINATION);

    const onChange = useCallback(
      (page, pageSize) => {
        setCurrentPagination({
          ...currentPagination,
          current: page,
          pageSize,
        });
      },
      [currentPagination, setCurrentPagination]
    );

    const pagination = useMemo(() => {
      if (isNil(currentPagination)) {
        return undefined;
      }
      return {
        ...currentPagination,
        onChange,
      };
    }, [currentPagination, onChange]);

    useEffect(() => {
      if (!isEqual(previous.current, currentPagination)) {
        previous.current = currentPagination;
        if (onPaginationChange) {
          onPaginationChange(currentPagination);
        }
      }
    }, [currentPagination]);

    useEffect(() => {
      if (isNil(incomePagination) || (isBoolean(incomePagination) && !incomePagination)) {
        setCurrentPagination(null);
        return () => {
          setCurrentPagination(DEFAULT_PAGINATION);
        };
      }
      setCurrentPagination({ ...DEFAULT_PAGINATION, ...incomePagination });
      return () => {
        setCurrentPagination(DEFAULT_PAGINATION);
      };
    }, [incomePagination]);

    return <Component {...rest} pagination={pagination} />;
  };
}
